// The time ledger: every change to logged time — added, edited, moved, deleted, a running timer pointed
// at another task — kept as its own record (who, when, before → after). Nothing here rewrites history:
// the task's timeEntries show what IS, the ledger shows how it got there (for when crew edit their own).
// Pure. Records live in `timeEdits/{id}` (org, opsTimeEdits).
export const TIME_EDIT_KINDS = ['add', 'edit', 'delete', 'move', 'retarget', 'session'];

const slim = (e) => (e ? { id: e.id || null, date: e.date || null, startTime: e.startTime || null, endTime: e.endTime || null, duration: e.duration || 0, note: e.note || null } : null);

/** One ledger record. `task` is where the time was (or is); `toTask` for move / retarget. */
export function timeEditRecord({ id, kind, task, toTask = null, before = null, after = null, by = null, reason = null, now = Date.now() }) {
  const b = slim(before);
  const a = slim(after);
  return {
    id, kind, at: now,
    by: by ? { uid: by.uid || null, name: by.name || by.email || null } : null,
    projectId: task?.projectId || toTask?.projectId || null,
    taskId: task?.id || null, taskTitle: task?.title || null,
    toTaskId: toTask?.id || null, toTaskTitle: toTask?.title || null,
    before: b, after: a,
    deltaMs: (a?.duration || 0) - (b?.duration || 0),
    reason: reason || null,
  };
}

const clockLabel = (e) => (e ? `${e.startTime || '?'}–${e.endTime || 'now'}${e.date ? ` on ${e.date}` : ''}` : '');
const mins = (ms) => { const m = Math.round(Math.abs(ms || 0) / 60000); return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`; };

/** Plain words for one record. */
export function describeTimeEdit(r) {
  if (!r) return '';
  switch (r.kind) {
    case 'add': return `Added ${clockLabel(r.after)} (${mins(r.after?.duration)}) to ${r.taskTitle}`;
    case 'delete': return `Deleted ${clockLabel(r.before)} (${mins(r.before?.duration)}) from ${r.taskTitle}`;
    case 'move': return `Moved ${clockLabel(r.before)} (${mins(r.before?.duration)}) from ${r.taskTitle} to ${r.toTaskTitle}`;
    case 'retarget': return `Switched the running timer${r.before?.startTime ? ` (since ${r.before.startTime})` : ''} from ${r.taskTitle} to ${r.toTaskTitle}`;
    case 'session': return `Changed the clock ${clockLabel(r.before)} → ${clockLabel(r.after)} on ${r.taskTitle}${r.deltaMs ? ` · ${r.deltaMs > 0 ? '+' : '−'}${mins(r.deltaMs)}` : ''}`;
    case 'edit': {
      const d = r.deltaMs ? ` · ${r.deltaMs > 0 ? '+' : '−'}${mins(r.deltaMs)}` : '';
      return `Changed ${clockLabel(r.before)} → ${clockLabel(r.after)} on ${r.taskTitle}${d}`;
    }
    default: return `${r.kind} on ${r.taskTitle}`;
  }
}

/** The records touching a task (as source or target), newest first. */
export function timeEditsFor(records, taskId) {
  return (records || []).filter((r) => r && (r.taskId === taskId || r.toTaskId === taskId)).sort((a, b) => (b.at || 0) - (a.at || 0));
}
